import { INPUT_QUOTE_MESSAGE, TOOL_QUOTE_MESSAGE } from '../../src/usage/quote-errors.mjs';
import { chatErrorCopy, type ChatErrorKey } from '@/i18n/ns/chat-error';

type ErrorCopy = Readonly<Record<ChatErrorKey, string>>;
type QuoteCopy = Pick<ErrorCopy, 'input' | 'tool'>;
function describe(error: unknown) {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  let body: { error?: string; code?: string; message?: string; reason?: unknown } | null =
    typeof error === 'object' && error !== null && !(error instanceof Error) ? error : null;
  try {
    body ??= JSON.parse(raw);
  } catch {
    /* Plain safe messages stay plain. */
  }
  return { raw, body };
}
function quoteMessage({ raw, body }: ReturnType<typeof describe>, copy: QuoteCopy) {
  if (
    raw === 'usage_turn_quote_unavailable' ||
    (body?.error ?? body?.code) === 'usage_turn_quote_unavailable' ||
    raw === INPUT_QUOTE_MESSAGE ||
    body?.message === INPUT_QUOTE_MESSAGE
  )
    return copy.input;
  if (
    raw === 'usage_turn_tool_quote_unavailable' ||
    (body?.error ?? body?.code) === 'usage_turn_tool_quote_unavailable' ||
    raw === TOOL_QUOTE_MESSAGE ||
    body?.message === TOOL_QUOTE_MESSAGE
  )
    return copy.tool;
  return null;
}
/** A tool card replaces only this known refusal; its other diagnostics retain their existing contract. */
export function quoteErrorMessage(
  error: unknown,
  copy: QuoteCopy = chatErrorCopy.en,
): string | null {
  return quoteMessage(describe(error), copy);
}
// The plan dialog opens with a refused send and offers the plan; the note only says why, in the dialog's words.
const PLAN_REFUSALS = {
  plan_required: 'planRequired',
  credits_exhausted: 'creditsExhausted',
  member_limit_reached: 'memberLimitReached',
} as const satisfies Record<string, ChatErrorKey>;
/** A send the plan refused (HTTP 402 payment_required, from the server or refused before sending). */
function planMessage({ raw, body }: ReturnType<typeof describe>, copy: ErrorCopy) {
  if (raw !== 'payment_required' && (body?.error ?? body?.code) !== 'payment_required') return null;
  const reason = typeof body?.reason === 'string' ? body.reason : '';
  return copy[
    Object.hasOwn(PLAN_REFUSALS, reason)
      ? PLAN_REFUSALS[reason as keyof typeof PLAN_REFUSALS]
      : 'paymentRequired'
  ];
}
/** Why a turn could not reach the App its chat edits: the chat can no longer name it, or its preview is gone (both
 * refused before sending), or the server could not read it just now. A saved App opens again from Apps; a gone preview
 * can also be left behind, and the App is built again from the chat. */
export function editingAppRefusal(error: unknown): 'lost' | 'gone' | 'unreachable' | null {
  const { raw, body } = describe(error);
  if (raw === 'editing_app_unavailable') return 'lost';
  if (raw === 'editing_preview_unavailable') return 'gone';
  return (body?.error ?? body?.code) === 'editing_app_unavailable' ? 'unreachable' : null;
}
export function chatErrorMessage(error: unknown, copy: ErrorCopy = chatErrorCopy.en): string {
  const description = describe(error);
  const quote = quoteMessage(description, copy);
  if (quote) return quote;
  const plan = planMessage(description, copy);
  if (plan) return plan;
  const editing = editingAppRefusal(error);
  if (editing)
    return {
      lost: copy.editingAppUnavailable,
      gone: copy.editingPreviewGone,
      unreachable: copy.editingAppUnreachable,
    }[editing];
  const { raw, body } = description;
  if (
    body?.error === 'FST_ERR_CTP_BODY_TOO_LARGE' ||
    raw.includes('Request body is too large') ||
    raw === 'HTTP 413'
  )
    return copy.tooLarge;
  if (typeof body?.message === 'string' && body.message.length <= 300) return body.message;
  if (body || !raw || raw.length > 300 || /^[a-z]+(?:_[a-z0-9]+)+$/.test(raw))
    return copy.unfinished;
  return raw;
}
